import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { DataTestId } from '@mews-commander/enums';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { OptimusPlainLink, useIsMobileOrSmallPhone } from '@mews-commander/ui';
import { anonymizeUrl } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { getSpacingToken, getTextTokenPreset, getToken, MEDIA_QUERY, Typography } from '@optimus-web/core';
import { trackEvent } from '../middleware';
import { navigateInModal } from '../modules/app/actions/common';

interface BaseBreadcrumb {
    text: React.ReactNode;
}

export interface ActiveBreadcrumb extends BaseBreadcrumb {
    active: true;
}

export interface ClickableBreadcrumb extends BaseBreadcrumb {
    url: string;
}

export type Breadcrumb = ActiveBreadcrumb | ClickableBreadcrumb;

export const isActive = (breadcrumb: Breadcrumb): breadcrumb is ActiveBreadcrumb =>
    (breadcrumb as ActiveBreadcrumb).active;

const BreadcrumbItem = (breadcrumb: Breadcrumb) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const isMobileOrSmallPhone = useIsMobileOrSmallPhone();

    if (isActive(breadcrumb)) {
        return (
            <ActiveElement
                textStyle={isMobileOrSmallPhone ? 'titleMediumStrong' : 'titleLargeStrong'}
                as="h1"
                data-test-id={DataTestId.ActiveBreadcrumb}
            >
                {breadcrumb.text}
            </ActiveElement>
        );
    }

    if (isMobileOrSmallPhone) {
        return null;
    }

    const { url, text } = breadcrumb;

    const handleClick = () => {
        dispatch(
            trackEvent({
                eventCategory: 'Breadcrumb',
                eventAction: 'link clicked',
                eventLabel: anonymizeUrl(url),
                eventValue: undefined,
            })
        );
        dispatch(navigateInModal(url));
    };

    return (
        <>
            <LinkElement href={url} onClick={handleClick}>
                {text}
            </LinkElement>
            <Separator>{UnicodeEntity.ForwardSlash}</Separator>
        </>
    );
};

const LinkElement = styled(OptimusPlainLink)`
    ${getTextTokenPreset('titleMedium')};
    color: ${getToken('textStaticSecondary')};
    height: ${getToken('sizing400')}px;
    border-radius: ${getToken('borderRadius100')}px;
    padding: 0 ${getSpacingToken('100')}px;
    display: block;
    align-items: center;
    text-decoration: none;
    box-sizing: border-box;
    white-space: nowrap;
    max-width: 300px;
    text-overflow: ellipsis;
    overflow: hidden;

    ${MEDIA_QUERY.tabletUp} {
        ${getTextTokenPreset('titleLarge')};
    }

    &:hover {
        background: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        color: ${getToken('textStaticPrimary')};
    }
`;

const Separator = styled.div`
    display: inline-block;
    margin: 0 4px;
    ${getTextTokenPreset('titleLarge')};
    color: ${getToken('textStaticSecondary')};
`;

const ActiveElement = styled(Typography)`
    ${MEDIA_QUERY.tabletUp} {
        padding: 0 ${getSpacingToken('100')}px;
    }
`;

export default BreadcrumbItem;
